import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  LogOut, Home, Users, DollarSign, User, ChevronDown, BarChart3, Menu, X,
  FileText, ClipboardList, Wallet, Building2, Briefcase, FileSignature, Database,
} from "lucide-react";
import { GlobalSearch } from "./GlobalSearch";

const OPERACION_ITEMS = [
  { path: "/cobranza", label: "Cobranza", icon: DollarSign },
  { path: "/egresos", label: "Egresos", icon: Wallet },
  { path: "/caratulas", label: "Carátulas", icon: FileText },
  { path: "/contratos", label: "Contratos", icon: FileSignature },
  { path: "/renovaciones", label: "Renovaciones", icon: ClipboardList },
  { path: "/documentos", label: "Documentos", icon: FileText },
  { path: "/cajas-chicas", label: "Cajas Chicas", icon: Wallet },
  { path: "/propietarios", label: "Propietarios", icon: Users },
];

export default function Layout({ user, onLogout, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showOperacion, setShowOperacion] = useState(false);
  const menuRef = useRef(null);
  const operacionRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (operacionRef.current && !operacionRef.current.contains(event.target)) {
        setShowOperacion(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        const hamburgerButton = event.target.closest('[data-mobile-menu-toggle]');
        if (!hamburgerButton) {
          setShowMobileMenu(false);
        }
      }
    };
    if (showUserMenu || showMobileMenu || showOperacion) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu, showMobileMenu, showOperacion]);

  const handleNavigation = (path) => {
    navigate(path);
    setShowMobileMenu(false);
    setShowOperacion(false);
  };

  const esPropietario = user.rol === "propietario";
  const enOperacion = OPERACION_ITEMS.some((it) => location.pathname.startsWith(it.path));
  const puedeVerReportes =
    user.rol === "maestro" ||
    (user.rol === "admin" && user.permisos?.includes("ver_reportes")) ||
    esPropietario;

  const rolLabel = user.rol === "maestro" ? "Maestro" : user.rol === "admin" ? "Administrador" : esPropietario ? "Propietario" : "Consulta";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-3">
            {/* Logo */}
            <div className="flex items-center shrink-0">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                data-testid="logo-button"
              >
                <img src="/logo.png" alt="ROTCO" className="h-10 w-auto" />
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="nav-inicio"
                className="text-sm"
              >
                <Home className="w-4 h-4 mr-2" />
                {esPropietario ? "Mis Contratos" : "Inicio"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/propiedades")}
                data-testid="nav-propiedades"
                className="text-sm"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Propiedades
              </Button>

              {esPropietario ? (
                <>
                  <Button variant="ghost" onClick={() => navigate("/cobranza")} data-testid="nav-cobranza" className="text-sm">
                    <DollarSign className="w-4 h-4 mr-2" /> Mi Cobranza
                  </Button>
                  <Button variant="ghost" onClick={() => navigate("/mis-gastos")} data-testid="nav-mis-gastos" className="text-sm">
                    <DollarSign className="w-4 h-4 mr-2" /> Mis Gastos
                  </Button>
                </>
              ) : (
                <div className="relative" ref={operacionRef}>
                  <Button
                    variant="ghost"
                    onClick={() => setShowOperacion(!showOperacion)}
                    data-testid="nav-operacion"
                    className={`text-sm ${enOperacion ? "bg-gray-100" : ""}`}
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Operación
                    <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${showOperacion ? "rotate-180" : ""}`} />
                  </Button>
                  {showOperacion && (
                    <div className="absolute left-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-testid="operacion-dropdown">
                      {OPERACION_ITEMS.map((it) => (
                        <button
                          key={it.path}
                          onClick={() => handleNavigation(it.path)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                            location.pathname.startsWith(it.path) ? "text-indigo-600 font-medium" : "text-gray-700"
                          }`}
                          data-testid={`operacion-${it.path.slice(1)}`}
                        >
                          <it.icon className="w-4 h-4" />
                          {it.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {puedeVerReportes && (
                <Button variant="ghost" onClick={() => navigate("/reportes")} data-testid="nav-reportes" className="text-sm">
                  <BarChart3 className="w-4 h-4 mr-2" /> Reportes
                </Button>
              )}
              {user.rol === "maestro" && (
                <Button variant="ghost" onClick={() => navigate("/usuarios")} data-testid="nav-usuarios" className="text-sm">
                  <Users className="w-4 h-4 mr-2" /> Usuarios
                </Button>
              )}
              {user.rol === "maestro" && (
                <Button variant="ghost" onClick={() => navigate("/base-maestra")} data-testid="nav-base-maestra" className="text-sm">
                  <Database className="w-4 h-4 mr-2" /> Base maestra
                </Button>
              )}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2 flex-1 lg:flex-none justify-end">
              {/* Buscador global - Desktop */}
              {!esPropietario && (
                <GlobalSearch className="hidden md:block w-56 xl:w-64" />
              )}

              {/* User Menu - Desktop */}
              <div className="hidden sm:block relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-medium text-gray-900" data-testid="user-name">{user.nombre}</span>
                    <span className="text-xs text-gray-500" data-testid="user-role">{rolLabel}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={() => { setShowUserMenu(false); navigate("/perfil"); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="menu-profile"
                    >
                      <User className="w-4 h-4" /> Mi Perfil
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={() => { setShowUserMenu(false); onLogout(); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4" /> Salir
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-mobile-menu-toggle
                data-testid="mobile-menu-toggle"
                aria-label="Toggle mobile menu"
              >
                {showMobileMenu ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div ref={mobileMenuRef} className="lg:hidden border-t border-gray-200 bg-white shadow-lg">
            <nav className="px-4 py-4 space-y-1">
              {/* User Info - Mobile */}
              <div className="sm:hidden pb-3 mb-3 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.nombre}</p>
                    <p className="text-xs text-gray-500">{rolLabel}</p>
                  </div>
                </div>
              </div>

              {/* Buscador global - Mobile */}
              {!esPropietario && (
                <div className="pb-2">
                  <GlobalSearch />
                </div>
              )}

              <button
                onClick={() => handleNavigation("/dashboard")}
                data-testid="mobile-nav-inicio"
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
              >
                <Home className="w-5 h-5" />
                <span className="text-base font-medium">{esPropietario ? "Mis Contratos" : "Inicio"}</span>
              </button>

              <button
                onClick={() => handleNavigation("/propiedades")}
                data-testid="mobile-nav-propiedades"
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
              >
                <Building2 className="w-5 h-5" />
                <span className="text-base font-medium">Propiedades</span>
              </button>

              {esPropietario ? (
                <>
                  <button
                    onClick={() => handleNavigation("/cobranza")}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                  >
                    <DollarSign className="w-5 h-5" />
                    <span className="text-base font-medium">Mi Cobranza</span>
                  </button>
                  <button
                    onClick={() => handleNavigation("/mis-gastos")}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                  >
                    <DollarSign className="w-5 h-5" />
                    <span className="text-base font-medium">Mis Gastos</span>
                  </button>
                </>
              ) : (
                <>
                  <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase text-gray-400">Operación</p>
                  {OPERACION_ITEMS.map((it) => (
                    <button
                      key={it.path}
                      onClick={() => handleNavigation(it.path)}
                      data-testid={`mobile-operacion-${it.path.slice(1)}`}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                    >
                      <it.icon className="w-5 h-5" />
                      <span className="text-base font-medium">{it.label}</span>
                    </button>
                  ))}
                </>
              )}

              {puedeVerReportes && (
                <button
                  onClick={() => handleNavigation("/reportes")}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                >
                  <BarChart3 className="w-5 h-5" />
                  <span className="text-base font-medium">Reportes</span>
                </button>
              )}

              {user.rol === "maestro" && (
                <button
                  onClick={() => handleNavigation("/usuarios")}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                >
                  <Users className="w-5 h-5" />
                  <span className="text-base font-medium">Usuarios</span>
                </button>
              )}

              {user.rol === "maestro" && (
                <button
                  onClick={() => handleNavigation("/base-maestra")}
                  data-testid="mobile-nav-base-maestra"
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                >
                  <Database className="w-5 h-5" />
                  <span className="text-base font-medium">Base maestra</span>
                </button>
              )}

              {/* Mobile User Actions */}
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-1">
                <button
                  onClick={() => handleNavigation("/perfil")}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-gray-700"
                >
                  <User className="w-5 h-5" />
                  <span className="text-base font-medium">Mi Perfil</span>
                </button>
                <button
                  onClick={() => { setShowMobileMenu(false); onLogout(); }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-red-50 flex items-center gap-3 text-red-600"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-base font-medium">Cerrar Sesión</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-500">
            Sistema de Gestión de Contratos de Arrendamiento © 2025
          </p>
        </div>
      </footer>
    </div>
  );
}
